/**
 * WaitlistByRole
 * ──────────────────────────────────────────────────────────────────────
 * Renders the Waitlist tab with role-specific layout and actions
 * (Edit 4 + Edit 5 of scheduler_edits_v2.md).
 *
 * Props:
 *   role:             'front_desk' | 'ma' | 'provider' | 'manager'
 *   patients:         [{ id, rank, probability, name, type, addedAt, daysWaiting,
 *                        arrival ('<15'|'15-30'|'30-60'|'60+'),
 *                        insuranceActive, showRate, careNeed,
 *                        bestMatch?, overbookLimit? }]
 *   pendingApprovals: [{ id, patientName, type, provider, date, overbookIndex,
 *                         overbookLimit, submittedBy, submittedMinAgo,
 *                         aiRecommendation, aiRationale }]
 *   analytics:        (manager-only) { totalToday, notified, booked, declined,
 *                        stillWaiting, avgWaitDays, slotsFilledThisWeek, revenueRecovered }
 *   onAdd(), onNotify(p), onBook(p), onRemove(p), onApprove(a), onDecline(a)
 *
 * Arrival probability display (spec-driven coloring):
 *   ≥80% green, 50–79% amber, <50% red.
 *
 * This component is pure UI from props — no banned literals live here.
 */
import { useMemo } from 'react';
import { Card, CardContent } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { cn } from '../../../../lib/utils';
import { Plus, Sparkles, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';

const arrivalLabel = (k) =>
  ({
    '<15': '< 15 min',
    '15-30': '15–30 min',
    '30-60': '30–60 min',
    '60+': 'More than an hour',
  }[k] || k);

const probColor = (p) =>
  p >= 80 ? 'text-emerald-600' : p >= 50 ? 'text-amber-600' : 'text-red-600';

const WaitlistCard = ({ p, actions, readOnly }) => (
  <Card className="border-slate-200" data-testid={`waitlist-card-${p.id}`}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span
            className="text-[16px] font-medium text-slate-900 shrink-0"
            data-testid={`waitlist-rank-${p.id}`}
          >
            #{p.rank}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">
              {p.name}
              {p.type ? (
                <span className="font-normal text-slate-500"> · {p.type}</span>
              ) : null}
            </div>
            <div className="text-[10px] text-slate-400">
              Waiting since: {p.addedAt} · {p.daysWaiting} day{p.daysWaiting === 1 ? '' : 's'}
            </div>
          </div>
        </div>
        <span
          className={cn('text-xs font-semibold shrink-0', probColor(p.probability))}
          data-testid={`waitlist-prob-${p.id}`}
        >
          {p.probability}% likely
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3 text-[10px]">
        <div>
          <span className="text-slate-400">Patient can arrive in: </span>
          <span className="text-slate-700 font-medium">{arrivalLabel(p.arrival)}</span>
        </div>
        <div>
          <span className="text-slate-400">Insurance: </span>
          <span
            className={cn(
              'font-medium',
              p.insuranceActive ? 'text-emerald-600' : 'text-amber-600'
            )}
          >
            {p.insuranceActive ? 'Active ✓' : 'Inactive'}
          </span>
        </div>
        <div>
          <span className="text-slate-400">Show rate: </span>
          <span className="text-slate-700 font-medium">{p.showRate}%</span>
        </div>
        <div>
          <span className="text-slate-400">Care need: </span>
          <span className="text-slate-700 font-medium">{p.careNeed || 'None'}</span>
        </div>
      </div>

      {p.bestMatch && (
        <div className="mt-3 pt-2 border-t border-slate-100 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3 text-violet-500" />
          <span className="text-[10px] text-violet-600 font-medium">
            Best slot match: {p.bestMatch}
          </span>
        </div>
      )}

      {readOnly ? (
        <p className="text-[10px] text-slate-400 mt-3">Read-only.</p>
      ) : (
        <div className="flex gap-1.5 mt-3">
          {actions}
        </div>
      )}
    </CardContent>
  </Card>
);

/* ── Role: Front Desk ── */
const FrontDeskView = ({ patients, onAdd, onNotify, onBook, onRemove }) => (
  <div className="space-y-3" data-testid="waitlist-fd">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-slate-800">Active Waitlist</span>
        <Badge className="text-[9px] border-0 bg-slate-100 text-slate-500">
          {patients.length}
        </Badge>
      </div>
      <Button
        onClick={onAdd}
        className="h-9 text-[13px] font-medium bg-[#111] hover:bg-[#222] text-white gap-1.5"
        style={{ borderRadius: 8 }}
        data-testid="waitlist-add-btn"
      >
        <Plus className="h-3.5 w-3.5" /> Add to waitlist
      </Button>
    </div>
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-violet-100 bg-violet-50/50"
      data-testid="waitlist-ranking-note"
    >
      <Sparkles className="h-3 w-3 text-violet-500 shrink-0" />
      <p className="text-[10px] text-violet-700">
        <b>AI-ranked</b> by each patient&apos;s ability to arrive quickly, show rate, active
        insurance, and overdue care needs. Highest match first.
      </p>
    </div>
    {patients.length === 0 && (
      <div className="py-10 text-center rounded-lg border border-dashed border-slate-200">
        <CheckCircle2 className="h-5 w-5 text-emerald-300 mx-auto mb-1" />
        <p className="text-[11px] text-slate-500">Waitlist is empty.</p>
      </div>
    )}
    <div className="space-y-2">
      {patients.map((p) => (
        <WaitlistCard
          key={p.id}
          p={p}
          actions={
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-[10px]"
                onClick={() => onNotify?.(p)}
                data-testid={`waitlist-notify-${p.id}`}
              >
                Notify patient
              </Button>
              <Button
                size="sm"
                className="h-7 text-[10px] bg-[#111] hover:bg-[#222]"
                onClick={() => onBook?.(p)}
                data-testid={`waitlist-book-${p.id}`}
              >
                Book &amp; notify
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[10px] text-red-500 hover:text-red-700"
                onClick={() => onRemove?.(p)}
                data-testid={`waitlist-remove-${p.id}`}
              >
                Remove
              </Button>
            </>
          }
        />
      ))}
    </div>
  </div>
);

/* ── Role: Provider (read-only) ── */
const ProviderView = ({ patients }) => (
  <div className="space-y-3" data-testid="waitlist-provider">
    <p className="text-[11px] text-slate-500 italic">
      Contact your front desk to manage waitlist patients.
    </p>
    <div className="space-y-2">
      {patients.map((p) => (
        <WaitlistCard key={p.id} p={p} readOnly />
      ))}
    </div>
  </div>
);

/* ── Role: Manager (two-panel) ── */
const ManagerView = ({ patients, pendingApprovals = [], analytics, onApprove, onDecline }) => (
  <div className="grid grid-cols-5 gap-3" data-testid="waitlist-manager">
    <div className="col-span-3 space-y-3" data-testid="waitlist-approval-queue">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
        <span className="text-sm font-semibold text-slate-800">Approval Queue</span>
        <Badge className="text-[9px] border-0 bg-amber-50 text-amber-700">
          {pendingApprovals.length}
        </Badge>
      </div>

      {pendingApprovals.length === 0 ? (
        <div className="p-4 rounded-lg border border-dashed border-slate-200 text-[11px] text-slate-500">
          No approvals needed right now — Echo is managing waitlist automatically.
        </div>
      ) : (
        pendingApprovals.map((a) => (
          <Card key={a.id} className="border-amber-200" data-testid={`approval-${a.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                <AlertTriangle className="h-3 w-3 text-amber-500" />
                Approval needed · Submitted {a.submittedMinAgo} min ago by {a.submittedBy}
              </div>
              <div className="text-sm font-medium text-slate-800">
                {a.patientName} · {a.type} · {a.provider} · {a.date}
              </div>
              <div className="text-[11px] text-slate-600">
                Would be overbooked patient #{a.overbookIndex} of {a.overbookLimit} for this
                provider/day
              </div>
              <div className="text-[11px] text-violet-700">
                Echo AI: {a.aiRecommendation} recommended — {a.aiRationale}
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  className="h-7 text-[10px] bg-[#111] hover:bg-[#222]"
                  onClick={() => onApprove?.(a)}
                  data-testid={`approval-approve-${a.id}`}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px]"
                  onClick={() => onDecline?.(a)}
                  data-testid={`approval-decline-${a.id}`}
                >
                  Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      <div className="pt-2">
        <p className="text-[10px] uppercase text-slate-400 font-semibold mb-2">
          Full Waitlist ({patients.length})
        </p>
        <div className="space-y-2">
          {patients.slice(0, 5).map((p) => (
            <WaitlistCard key={p.id} p={p} readOnly />
          ))}
        </div>
      </div>
    </div>

    <div className="col-span-2 space-y-3" data-testid="waitlist-analytics-strip">
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-2">
          <div className="text-[10px] uppercase text-slate-400 font-semibold">
            Today&apos;s waitlist
          </div>
          <div className="text-2xl font-semibold text-slate-900">
            {analytics?.totalToday ?? '—'}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[10px] pt-2 border-t border-slate-100">
            <div>
              <span className="text-slate-400">Notified:</span>{' '}
              <span className="font-medium text-slate-800">{analytics?.notified ?? '—'}</span>
            </div>
            <div>
              <span className="text-slate-400">Booked:</span>{' '}
              <span className="font-medium text-slate-800">{analytics?.booked ?? '—'}</span>
            </div>
            <div>
              <span className="text-slate-400">Declined:</span>{' '}
              <span className="font-medium text-slate-800">{analytics?.declined ?? '—'}</span>
            </div>
            <div>
              <span className="text-slate-400">Still waiting:</span>{' '}
              <span className="font-medium text-slate-800">{analytics?.stillWaiting ?? '—'}</span>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-1.5 text-[11px]">
          <div className="flex justify-between">
            <span className="text-slate-500">Avg wait to placement</span>
            <span className="font-medium text-slate-800">
              {analytics?.avgWaitDays ?? '—'} days
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Slots filled this week</span>
            <span className="font-medium text-slate-800">
              {analytics?.slotsFilledThisWeek ?? '—'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Revenue recovered</span>
            <span className="font-medium text-emerald-600">
              {analytics?.revenueRecovered ?? '—'}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  </div>
);

const WaitlistByRole = ({
  role,
  patients = [],
  pendingApprovals = [],
  analytics = null,
  onAdd,
  onNotify,
  onBook,
  onRemove,
  onApprove,
  onDecline,
}) => {
  const sorted = useMemo(
    () =>
      [...patients].sort(
        (a, b) => (b.probability || 0) - (a.probability || 0)
      ).map((p, i) => ({ ...p, rank: i + 1 })),
    [patients]
  );

  if (role === 'manager') {
    return (
      <ManagerView
        patients={sorted}
        pendingApprovals={pendingApprovals}
        analytics={analytics}
        onApprove={onApprove}
        onDecline={onDecline}
      />
    );
  }
  if (role === 'provider') {
    return <ProviderView patients={sorted} />;
  }
  // MA role: the caller should not mount this tab at all.
  return (
    <FrontDeskView
      patients={sorted}
      onAdd={onAdd}
      onNotify={onNotify}
      onBook={onBook}
      onRemove={onRemove}
    />
  );
};

export default WaitlistByRole;
